
import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Box,
  Grid,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Divider,
  CircularProgress,
  Autocomplete,
  Chip,
  Tooltip,
  useMediaQuery,
  useTheme,
  Collapse,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  Description as FileIcon, ExpandMore,
  ExpandLess,
} from "@mui/icons-material";
import {
  getIssues,
  exportIssueItems,
  DivisionList,
} from "../../../Services/Services";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import InfiniteScroll from "react-infinite-scroll-component";
import { useLocation } from "react-router-dom";

function Material_issue_register() {
  // ===== States =====
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [expandedIssue, setExpandedIssue] = useState(null);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [divisionLists, setDivisionLists] = useState([]);
  const [selectedDivision, setSelectedDivision] = useState(null);

  // Date Range States
  const [customDateRange, setCustomDateRange] = useState({ from: "", to: "" });
  const [dateMenuAnchor, setDateMenuAnchor] = useState(null);
  const [showCustomDateInputs, setShowCustomDateInputs] = useState(false);
  const [tempCustomDateRange, setTempCustomDateRange] = useState({ from: "", to: "" });
  const [excelLoading, setExcelLoading] = useState(false);

  const [filters, setFilters] = useState({
    slip_no: "",
    project_name: "",
    date_range: "today",
  });

  const location = useLocation();

  // ===== Helper Functions =====
  const formatDateDisplay = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  const getDateRangeDisplay = (rangeType) => {
    const now = new Date();
    const formatDate = (date) => {
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };

    switch (rangeType) {
      case "today":
        return formatDate(now);
      case "yesterday":
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        return formatDate(yesterday);
      case "this_week":
        const weekStart = new Date(now);
        weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1);
        return `${formatDate(weekStart)} to ${formatDate(now)}`;
      case "previous_week":
        const prevWeekEnd = new Date(now);
        prevWeekEnd.setDate(prevWeekEnd.getDate() - prevWeekEnd.getDay());
        const prevWeekStart = new Date(prevWeekEnd);
        prevWeekStart.setDate(prevWeekStart.getDate() - 6);
        return `${formatDate(prevWeekStart)} to ${formatDate(prevWeekEnd)}`;
      case "this_month":
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        return `${formatDate(monthStart)} to ${formatDate(now)}`;
      case "current_year":
        const yearStart = new Date(now.getFullYear(), 0, 1);
        return `${formatDate(yearStart)} to ${formatDate(now)}`;
      case "financial_year":
        let fyStart;
        if (now.getMonth() >= 3) {
          fyStart = new Date(now.getFullYear(), 3, 1);
        } else {
          fyStart = new Date(now.getFullYear() - 1, 3, 1);
        }
        return `${formatDate(fyStart)} to ${formatDate(now)}`;
      default:
        return "";
    }
  };

  function formatOnlyDate(dateTimeString) {
  if (!dateTimeString) return "N/A";

  // Take only the date part (before T)
  const datePart = dateTimeString.split("T")[0];  // "2025-12-02"

  const [year, month, day] = datePart.split("-");

  return `${day}-${month}-${year}`;
}

  // ===== Fetch Division List =====
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await DivisionList();
        let divisions = [];
        if (Array.isArray(response)) {
          divisions = response;
        } else if (response.results) {
          divisions = response.results;
        } else if (response.data) {
          divisions = response.data;
        }
        setDivisionLists(divisions || []);
      } catch (error) {
        console.error("Division fetch error:", error);
        toast.error("Failed to load divisions");
      }
    };
    fetchData();
  }, []);

  // ===== Excel Export =====
  const handleExcelExport = async () => {
    setExcelLoading(true);
    try {
      const exportFilters = { ...filters };

      if (selectedDivision?.division_id) {
        exportFilters.division_id = selectedDivision.division_id;
      }

      if (
        filters.date_range === "custom" &&
        customDateRange.from &&
        customDateRange.to
      ) {
        exportFilters.from_date = customDateRange.from;
        exportFilters.to_date = customDateRange.to;
      }

      const blob = await exportIssueItems(exportFilters);

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Material_issue_register_${new Date().toISOString().split("T")[0]
        }.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success("Excel downloaded successfully!");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export Excel");
    } finally {
      setExcelLoading(false);
    }
  };

  // ===== Fetch Function =====
  const fetchItems = async (pageNum = 1, reset = false) => {
    setLoading(true);
    try {
      const params = { ...filters };

      if (selectedDivision?.division_id) {
        params.division_id = selectedDivision.division_id;
      }

      if (
        filters.date_range === "custom" &&
        customDateRange.from &&
        customDateRange.to
      ) {
        params.from_date = customDateRange.from;
        params.to_date = customDateRange.to;
      }

      console.log("📤 Material Issue API Params:", params);

      const data = await getIssues(pageNum, params);

      if (reset || pageNum === 1) {
        setItems(data.results || []);
      } else {
        setItems((prev) => [...prev, ...(data.results || [])]);
      }

      setHasMore(!!data.next);
      setPage(pageNum);
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Failed to load issues");
    } finally {
      setLoading(false);
    }
  };

  // ===== Calculate Grand Total =====
  const grandTotal = useMemo(() => {
    let totalQty = 0;

    items.forEach((issue) => {
      (issue.items || []).forEach((item) => {
        totalQty += item.issue_qty || 0;
      });
    });

    return { totalQty };
  }, [items]);

  // ===== Total Row Count for Infinite Scroll =====
  const totalRowCount = useMemo(() => {
    return items.reduce((sum, issue) => sum + (issue.items?.length || 0), 0);
  }, [items]);

  // ===== useEffects =====
  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      fetchItems(1, true);
    }, 500);

    return () => clearTimeout(handler);
  }, [filters, selectedDivision, customDateRange]);

  useEffect(() => {
    if (location.state?.edited) {
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);



  // ===== Handlers =====
  const fetchMoreData = () => {
    if (hasMore && !loading) {
      const nextPage = page + 1;
      fetchItems(nextPage, false);
    }
  };

  const handleReset = () => {
    setFilters({
      slip_no: "",
      project_name: "",
      date_range: "this_month",
    });
    setSelectedDivision(null);
    setCustomDateRange({ from: "", to: "" });
    setTempCustomDateRange({ from: "", to: "" }); // ✅ Add
    setShowCustomDateInputs(false); // ✅ Add
    setItems([]);
    setPage(1);
    setHasMore(true);
  };

  // ✅ Handle Custom Date Range Apply
  const handleApplyCustomDateRange = () => {
    if (tempCustomDateRange.from && tempCustomDateRange.to) {
      setCustomDateRange({
        from: tempCustomDateRange.from,
        to: tempCustomDateRange.to,
      });
      setFilters((prev) => ({ ...prev, date_range: "custom" }));
      setDateMenuAnchor(null);
      setShowCustomDateInputs(false);
    } else {
      toast.warning("Please select both From and To dates");
    }
  };

  // ✅ Handle Cancel Custom Date Range
  const handleCancelCustomDateRange = () => {
    setTempCustomDateRange({ from: "", to: "" });
    setShowCustomDateInputs(false);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  // return (
  //   <Box>
  //     <ToastContainer position="top-right" autoClose={3000} />

  //     {/* ========== FILTER SECTION ========== */}
  //     <Box sx={{ mb: 2, mt: -5 }}>
  //       <Grid container spacing={2} alignItems="center">
  //         {/* Date Range */}
  //         <Grid item xs={12} sm={2.5}>
  //           <TextField
  //             label="Date Range"
  //             value={
  //               filters.date_range === "custom" &&
  //               customDateRange.from &&
  //               customDateRange.to
  //                 ? `${formatDateDisplay(
  //                     customDateRange.from
  //                   )} to ${formatDateDisplay(customDateRange.to)}`
  //                 : filters.date_range === "today"
  //                 ? "Today"
  //                 : filters.date_range === "yesterday"
  //                 ? "Yesterday"
  //                 : filters.date_range === "this_week"
  //                 ? "This Week"
  //                 : filters.date_range === "previous_week"
  //                 ? "Previous Week"
  //                 : filters.date_range === "this_month"
  //                 ? "This Month"
  //                 : filters.date_range === "current_year"
  //                 ? "Current Year"
  //                 : filters.date_range === "financial_year"
  //                 ? "Financial Year"
  //                 : "Select Date Range"
  //             }
  //             onClick={(e) => setDateMenuAnchor(e.currentTarget)}
  //             size="small"
  //             InputProps={{
  //               readOnly: true,
  //               endAdornment: (
  //                 <IconButton size="small" edge="end">
  //                   <ArrowDropDownIcon />
  //                 </IconButton>
  //               ),
  //             }}
  //             sx={{
  //               cursor: "pointer",
  //               "& .MuiInputBase-root": { height: 35, borderRadius: 0 },
  //               "& .MuiInputLabel-root": { fontSize: "0.7rem" },
  //             }}
  //             fullWidth
  //           />
  //         </Grid>

  //         {/* Division */}
  //         <Grid item xs={12} sm={2.5}>
  //           <Autocomplete
  //             disablePortal
  //             options={divisionLists || []}
  //             value={selectedDivision}
  //             onChange={(e, newVal) => setSelectedDivision(newVal)}
  //             getOptionLabel={(opt) => opt?.division || ""}
  //             isOptionEqualToValue={(opt, val) =>
  //               opt?.division_id === val?.division_id
  //             }
  //             renderInput={(params) => (
  //               <TextField
  //                 {...params}
  //                 label="Division"
  //                 size="small"
  //                 sx={{
  //                   "& .MuiInputBase-root": { height: 35, borderRadius: 0 },
  //                   "& .MuiInputLabel-root": { fontSize: "0.7rem" },
  //                 }}
  //               />
  //             )}
  //           />
  //         </Grid>

  //         {/* Issue No */}
  //         <Grid item xs={12} sm={2.5}>
  //           <TextField
  //             label="Rew. Slip No"
  //             name="slip_no"
  //             size="small"
  //             value={filters.slip_no}
  //             onChange={handleFilterChange}
  //             sx={{
  //               "& .MuiInputBase-root": { height: 35, borderRadius: 0 },
  //               "& .MuiInputLabel-root": { fontSize: "0.7rem" },
  //             }}
  //             fullWidth
  //           />
  //         </Grid>

  //         {/* Project */}
  //         <Grid item xs={12} sm={2.5}>
  //           <TextField
  //             label="Project"
  //             name="project_name"
  //             size="small"
  //             value={filters.project_name}
  //             onChange={handleFilterChange}
  //             sx={{
  //               "& .MuiInputBase-root": { height: 35, borderRadius: 0 },
  //               "& .MuiInputLabel-root": { fontSize: "0.7rem" },
  //             }}
  //             fullWidth
  //           />
  //         </Grid>

  //         {/* Reset Button */}
  //         <Grid item xs={12} sm={1}>
  //           <Button
  //             variant="contained"
  //             color="primary"
  //             size="small"
  //             onClick={handleReset}
  //             sx={{
  //               height: "35px",
  //               whiteSpace: "nowrap",
  //               borderRadius: "0",
  //               fontSize: "0.7rem",
  //             }}
  //             fullWidth
  //           >
  //             Reset
  //           </Button>
  //         </Grid>

  //         {/* Excel Export */}
  //         <Grid item xs={12} sm={1}>
  //           <Tooltip
  //             title={excelLoading ? "Exporting..." : "Download Excel"}
  //             arrow
  //           >
  //             <span>
  //               <IconButton
  //                 color="success"
  //                 size="small"
  //                 onClick={handleExcelExport}
  //                 disabled={excelLoading}
  //                 sx={{

  //                   borderRadius: "4px",
  //                   height: "35px",
  //                   width: "35px",
  //                 }}
  //               >
  //                 {excelLoading ? (
  //                   <CircularProgress size={20} color="inherit" />
  //                 ) : (
  //                   <FileIcon fontSize="small" />
  //                 )}
  //               </IconButton>
  //             </span>
  //           </Tooltip>
  //         </Grid>
  //       </Grid>
  //     </Box>

  //     {/* ========== HIDDEN DATE INPUT ========== */}
  //     <input
  //       ref={dateInputRef}
  //       type="date"
  //       style={{
  //         position: "absolute",
  //         opacity: 0,
  //         pointerEvents: "none",
  //         width: "1px",
  //         height: "1px",
  //       }}
  //       value={
  //         selectingDate === "from" ? customDateRange.from : customDateRange.to
  //       }
  //       min={selectingDate === "to" ? customDateRange.from : undefined}
  //       max={new Date().toISOString().split("T")[0]}
  //       onChange={(e) => {
  //         const selectedDate = e.target.value;

  //         if (selectingDate === "from") {
  //           setCustomDateRange((prev) => ({ ...prev, from: selectedDate }));
  //           setSelectingDate("to");
  //           setTimeout(() => dateInputRef.current?.showPicker(), 100);
  //         } else if (selectingDate === "to") {
  //           setCustomDateRange((prev) => ({ ...prev, to: selectedDate }));
  //           setFilters((prev) => ({ ...prev, date_range: "custom" }));
  //           setSelectingDate(null);
  //           setDateMenuAnchor(null);
  //         }
  //       }}
  //     />

  //     {/* ========== DATE RANGE MENU ========== */}
  //     <Menu
  //       anchorEl={dateMenuAnchor}
  //       open={Boolean(dateMenuAnchor)}
  //       onClose={() => setDateMenuAnchor(null)}
  //       PaperProps={{ sx: { width: 320 } }}
  //     >
  //       {[
  //         { key: "today", label: "Today" },
  //         { key: "yesterday", label: "Yesterday" },
  //         { key: "this_week", label: "This Week" },
  //         { key: "previous_week", label: "Previous Week" },
  //         { key: "this_month", label: "This Month" },
  //         { key: "current_year", label: "Current Year" },
  //         { key: "financial_year", label: "Financial Year" },
  //       ].map((range) => (
  //         <MenuItem
  //           key={range.key}
  //           onClick={() => {
  //             setFilters((prev) => ({ ...prev, date_range: range.key }));
  //             setCustomDateRange({ from: "", to: "" });
  //             setDateMenuAnchor(null);
  //           }}
  //         >
  //           <Box
  //             sx={{
  //               display: "flex",
  //               justifyContent: "space-between",
  //               width: "100%",
  //             }}
  //           >
  //             <Typography variant="body2">{range.label}</Typography>
  //             <Typography variant="caption" color="text.secondary">
  //               {getDateRangeDisplay(range.key)}
  //             </Typography>
  //           </Box>
  //         </MenuItem>
  //       ))}

  //       <Divider />

  //       <MenuItem
  //         onClick={() => {
  //           setDateMenuAnchor(null);
  //           setSelectingDate("from");
  //           setTimeout(() => dateInputRef.current?.showPicker(), 100);
  //         }}
  //       >
  //         <Typography variant="body2">Custom Date Range</Typography>
  //       </MenuItem>
  //     </Menu>

  //     {/* ========== TABLE WITH INFINITE SCROLL ========== */}
  //     <InfiniteScroll
  //       dataLength={totalRowCount}
  //       next={fetchMoreData}
  //       hasMore={hasMore}
  //       scrollableTarget="item-table"
  //       loader={
  //         loading && (
  //           <Box sx={{ textAlign: "center", p: 2 }}>
  //             <CircularProgress size={24} />
  //           </Box>
  //         )
  //       }
  //       endMessage={
  //         items.length > 0 && (
  //           <Typography
  //             sx={{ textAlign: "center", p: 2, color: "text.secondary" }}
  //           >
  //             No more data
  //           </Typography>
  //         )
  //       }
  //     >
  //       <TableContainer
  //         component={Paper}
  //         id="item-table"
  //         sx={{
  //           maxHeight: "72vh",
  //           overflowX: { xs: "auto", sm: "unset" },
  //           overflowY: "auto",
  //           width: "100%",
  //         }}
  //       >
  //         <Table
  //           stickyHeader
  //           size="small"
  //           sx={{
  //             minWidth: "max-content",
  //             "& td, & th": {
  //               borderBottom: "1px solid #ccc",
  //               textAlign: "center",
  //               whiteSpace: "nowrap",
  //             },
  //           }}
  //         >
  //           {/* ========== TABLE HEAD ========== */}
  //           <TableHead
  //             sx={{
  //               "& th": {
  //                 fontWeight: "bold",
  //                 color: (theme) =>
  //                   theme.palette.mode === "dark" ? "#2196f3" : "#333",
  //                 backgroundColor: (theme) =>
  //                   theme.palette.mode === "dark" ? "black" : "#f5f5f5",
  //               },
  //             }}
  //           >
  //             <TableRow>
  //               <TableCell
  //                 sx={{
  //                   position: "sticky",
  //                   left: 0,
  //                   backgroundColor: "#f5f5f5",
  //                   zIndex: 3,
  //                   minWidth: 50,
  //                   padding: "8px",
  //                 }}
  //               >
  //                 S. No.
  //               </TableCell>
  //               <TableCell>Date</TableCell>
  //               <TableCell>Req. Slip No</TableCell>
  //               <TableCell>Division</TableCell>
  //                <TableCell>Project</TableCell>
  //               <TableCell>Category</TableCell>
  //               <TableCell>Sub Category</TableCell>
  //               <TableCell>Item Type</TableCell>
  //               <TableCell>Item Name</TableCell>
  //               <TableCell align="right">Qty Issued</TableCell>
  //               <TableCell>Unit</TableCell>


  //             </TableRow>
  //           </TableHead>

  //           {/* ========== TABLE BODY ========== */}
  //           <TableBody>
  //             {loading && items.length === 0 ? (
  //               <TableRow>
  //                 <TableCell colSpan={12} align="center" sx={{ py: 8 }}>
  //                   <CircularProgress size={40} />
  //                   <Typography variant="body2" sx={{ mt: 2 }}>
  //                     Loading data...
  //                   </Typography>
  //                 </TableCell>
  //               </TableRow>
  //             ) : items.length === 0 ? (
  //               <TableRow>
  //                 <TableCell colSpan={12} align="center" sx={{ py: 8 }}>
  //                   <Box>
  //                     <Typography variant="h6" color="text.secondary">
  //                      No Data Found
  //                     </Typography>

  //                   </Box>
  //                 </TableCell>
  //               </TableRow>
  //             ) : (
  //               <>
  //                 {(() => {
  //                   let issueSerialNo = 0; // 🆕 Issue-level serial number

  //                   return items.flatMap((issue) => {
  //                     const itemsList = issue.items || [];
  //                     issueSerialNo++; // 🆕 Increment once per issue

  //                     return itemsList.map((item, itemIdx) => {
  //                       const isFirstItem = itemIdx === 0;

  //                       return (
  //                         <TableRow
  //                           key={`${issue.id}-${itemIdx}`}
  //                           hover
  //                           sx={{
  //                             backgroundColor:
  //                               issueSerialNo % 2 === 0 ? "#fafafa" : "#ffffff",
  //                             "&:hover": {
  //                               backgroundColor: "#e3f2fd",
  //                               "& td": {
  //                                 backgroundColor: "#e3f2fd !important",
  //                               },
  //                             },
  //                             "& td": {
  //                               paddingTop: "4px !important",
  //                               paddingBottom: "4px !important",
  //                               fontSize: { xs: "0.75rem", sm: "0.8125rem" },
  //                               whiteSpace: "nowrap",
  //                               color: (theme) =>
  //                                 theme.palette.mode === "dark"
  //                                   ? "white"
  //                                   : "inherit",
  //                             },
  //                           }}
  //                         >
  //                           {/* 🆕 Serial Number - Rowspan for first item */}
  //                           {isFirstItem && (
  //                             <TableCell
  //                               rowSpan={itemsList.length}
  //                               align="center"
  //                               sx={{
  //                                 position: "sticky",
  //                                 left: 0,
  //                                 backgroundColor:
  //                                   issueSerialNo % 2 === 0
  //                                     ? "#fafafa"
  //                                     : "#ffffff",
  //                                 zIndex: 2,
  //                                 fontWeight: "600",
  //                                 verticalAlign: "middle",
  //                               }}
  //                             >
  //                               {issueSerialNo}
  //                             </TableCell>
  //                           )}

  //                           {/* Date - Rowspan */}
  //                           {isFirstItem && (
  //                             <TableCell
  //                               rowSpan={itemsList.length}
  //                               sx={{ verticalAlign: "middle" }}
  //                             >
  //                               {formatOnlyDate(issue.created_at)}
  //                             </TableCell>
  //                           )}

  //                           {/* Issue No - Rowspan */}
  //                           {isFirstItem && (
  //                             <TableCell
  //                               rowSpan={itemsList.length}
  //                               sx={{
  //                                 verticalAlign: "middle",

  //                               }}
  //                             >
  //                               <Tooltip
  //                                 title={`${itemsList.length} item(s)`}
  //                                 arrow
  //                               >
  //                                 <span>{issue.slip_no}</span>
  //                               </Tooltip>
  //                             </TableCell>
  //                           )}

  //                           {/* Division - Rowspan */}
  //                           {isFirstItem && (
  //                             <TableCell
  //                               rowSpan={itemsList.length}
  //                               sx={{ verticalAlign: "middle" }}
  //                             >
  //                               {issue.division_name}
  //                             </TableCell>
  //                           )}

  //                           {/* Project - Rowspan */}
  //                           {isFirstItem && (
  //                             <TableCell
  //                               rowSpan={itemsList.length}
  //                               sx={{ verticalAlign: "middle" }}
  //                             >
  //                               {issue.project_name || "-"}
  //                             </TableCell>
  //                           )}
  //                           {/* Item Details */}
  //                           <TableCell>{item.category_name || "-"}</TableCell>
  //                           <TableCell>
  //                             {item.sub_category_name || "-"}
  //                           </TableCell>
  //                           <TableCell>{item.item_type || "-"}</TableCell>
  //                           <TableCell>
  //                             <Tooltip title={item.item_name} arrow>
  //                               <span>{item.item_name || "-"}</span>
  //                             </Tooltip>
  //                           </TableCell>
  //                           <TableCell align="right" sx={{ fontWeight: "500" }}>
  //                             {item.issue_qty || 0}
  //                           </TableCell>
  //                           <TableCell>{item.uom_name || "-"}</TableCell>


  //                           {/* Issued By - Rowspan */}
  //                           {/* {isFirstItem && (
  //                             <TableCell
  //                               rowSpan={itemsList.length}
  //                               sx={{ verticalAlign: "middle" }}
  //                             >
  //                               {issue.created_by_name || "-"}
  //                             </TableCell>
  //                           )} */}
  //                         </TableRow>
  //                       );
  //                     });
  //                   });
  //                 })()}

  //                 {/* ========== GRAND TOTAL ROW ========== */}

  //               </>
  //             )}
  //           </TableBody>
  //         </Table>
  //       </TableContainer>
  //     </InfiniteScroll>
  //   </Box>
  // );
  return (
    <Box>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* ========== FILTER SECTION ========== */}
      <Box sx={{ mb: 2, mt: -5 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Date Range */}
          <Grid item xs={12} sm={2.5}>
            <TextField
              label="Date Range"
              value={
                filters.date_range === "custom" &&
                  customDateRange.from &&
                  customDateRange.to
                  ? `${formatDateDisplay(
                    customDateRange.from
                  )} to ${formatDateDisplay(customDateRange.to)}`
                  : filters.date_range === "today"
                    ? "Today"
                    : filters.date_range === "yesterday"
                      ? "Yesterday"
                      : filters.date_range === "this_week"
                        ? "This Week"
                        : filters.date_range === "previous_week"
                          ? "Previous Week"
                          : filters.date_range === "this_month"
                            ? "This Month"
                            : filters.date_range === "current_year"
                              ? "Current Year"
                              : filters.date_range === "financial_year"
                                ? "Financial Year"
                                : "Select Date Range"
              }
              onClick={(e) => setDateMenuAnchor(e.currentTarget)}
              size="small"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <IconButton size="small" edge="end">
                    <ArrowDropDownIcon />
                  </IconButton>
                ),
              }}
              sx={{
                cursor: "pointer",
                "& .MuiInputBase-root": { height: 35, borderRadius: 0 },
                "& .MuiInputLabel-root": { fontSize: "0.7rem" },
              }}
              fullWidth
            />
          </Grid>

          {/* Division */}
          {/* <Grid item xs={12} sm={2.5}>
            <Autocomplete
              disablePortal
              options={divisionLists || []}
              value={selectedDivision}
              onChange={(e, newVal) => setSelectedDivision(newVal)}
              getOptionLabel={(opt) => opt?.division || ""}
              isOptionEqualToValue={(opt, val) =>
                opt?.division_id === val?.division_id
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Division"
                  size="small"
                  sx={{
                    "& .MuiInputBase-root": { height: 35, borderRadius: 0 },
                    "& .MuiInputLabel-root": { fontSize: "0.7rem" },
                  }}
                />
              )}
            />
          </Grid> */}

          {/* Slip No */}
          <Grid item xs={12} sm={2.5}>
            <TextField
              label="Req. Slip No"
              name="slip_no"
              size="small"
              value={filters.slip_no}
              onChange={handleFilterChange}
              sx={{
                "& .MuiInputBase-root": { height: 35, borderRadius: 0 },
                "& .MuiInputLabel-root": { fontSize: "0.7rem" },
              }}
              fullWidth
            />
          </Grid>

          {/* Project */}
          <Grid item xs={12} sm={2.5}>
            <TextField
              label="Project"
              name="project_name"
              size="small"
              value={filters.project_name}
              onChange={handleFilterChange}
              sx={{
                "& .MuiInputBase-root": { height: 35, borderRadius: 0 },
                "& .MuiInputLabel-root": { fontSize: "0.7rem" },
              }}
              fullWidth
            />
          </Grid>

          {/* Reset Button */}
          <Grid item xs={12} sm={1}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleReset}
              sx={{
                height: "35px",
                whiteSpace: "nowrap",
                borderRadius: "0",
                fontSize: "0.7rem",
              }}
              fullWidth
            >
              Reset
            </Button>
          </Grid>

          {/* Excel Export */}
          <Grid item xs={12} sm={1}>
            <Tooltip
              title={excelLoading ? "Exporting..." : "Download Excel"}
              arrow
            >
              <span>
                <IconButton
                  color="success"
                  size="small"
                  onClick={handleExcelExport}
                  disabled={excelLoading}
                  sx={{
                    borderRadius: "4px",
                    height: "35px",
                    width: "35px",
                  }}
                >
                  {excelLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <FileIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Grid>
        </Grid>
      </Box>

      {/* ========== DATE RANGE MENU - UPDATED ========== */}
      <Menu
        anchorEl={dateMenuAnchor}
        open={Boolean(dateMenuAnchor)}
        onClose={() => {
          setDateMenuAnchor(null);
          setShowCustomDateInputs(false);
          setTempCustomDateRange({ from: "", to: "" });
        }}
        PaperProps={{ sx: { width: 350, maxHeight: 500 } }}
      >
        {[
          { key: "today", label: "Today" },
          { key: "yesterday", label: "Yesterday" },
          { key: "this_week", label: "This Week" },
          { key: "previous_week", label: "Previous Week" },
          { key: "this_month", label: "This Month" },
          { key: "current_year", label: "Current Year" },
          { key: "financial_year", label: "Financial Year" },
        ].map((range) => (
          <MenuItem
            key={range.key}
            onClick={() => {
              setFilters((prev) => ({ ...prev, date_range: range.key }));
              setCustomDateRange({ from: "", to: "" });
              setDateMenuAnchor(null);
              setShowCustomDateInputs(false);
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Typography variant="body2">{range.label}</Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.65rem" }}
              >
                {getDateRangeDisplay(range.key)}
              </Typography>
            </Box>
          </MenuItem>
        ))}

        <Divider />

        {/* ✅ Custom Date Range - Expandable */}
        <MenuItem
          onClick={() => setShowCustomDateInputs(!showCustomDateInputs)}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: showCustomDateInputs ? "#e3f2fd" : "inherit",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: showCustomDateInputs ? 600 : 400 }}>
            Custom Date Range
          </Typography>
          {showCustomDateInputs ? <ExpandLess /> : <ExpandMore />}
        </MenuItem>

        {/* ✅ Both Date Pickers Together */}
        <Collapse in={showCustomDateInputs} timeout="auto" unmountOnExit>
          <Box
            sx={{
              px: 2,
              py: 2,
              bgcolor: "#f5f5f5",
              borderTop: "1px solid #e0e0e0",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
              Select Date Range
            </Typography>

            {/* From and To Date Inputs Side by Side */}
            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
              <TextField
                label="From Date"
                type="date"
                size="small"
                value={tempCustomDateRange.from}
                onChange={(e) =>
                  setTempCustomDateRange((prev) => ({
                    ...prev,
                    from: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max: tempCustomDateRange.to || new Date().toISOString().split("T")[0],
                }}
                sx={{
                  flex: 1,
                  "& .MuiInputBase-root": {
                    height: 40,
                    fontSize: "0.75rem",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.75rem",
                  },
                }}
              />

              <TextField
                label="To Date"
                type="date"
                size="small"
                value={tempCustomDateRange.to}
                onChange={(e) =>
                  setTempCustomDateRange((prev) => ({
                    ...prev,
                    to: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: tempCustomDateRange.from || undefined,
                  max: new Date().toISOString().split("T")[0],
                }}
                sx={{
                  flex: 1,
                  "& .MuiInputBase-root": {
                    height: 40,
                    fontSize: "0.75rem",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.75rem",
                  },
                }}
              />
            </Box>

            {/* Selected Range Preview */}
            {tempCustomDateRange.from && tempCustomDateRange.to && (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 1.5,
                  p: 1,
                  bgcolor: "#e8f5e9",
                  borderRadius: 1,
                  textAlign: "center",
                  color: "#2e7d32",
                  fontWeight: 500,
                }}
              >
                📅 {formatDateDisplay(tempCustomDateRange.from)} to{" "}
                {formatDateDisplay(tempCustomDateRange.to)}
              </Typography>
            )}

            {/* Apply and Cancel Buttons */}
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleCancelCustomDateRange}
                sx={{
                  fontSize: "0.7rem",
                  textTransform: "none",
                  borderRadius: 1,
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleApplyCustomDateRange}
                disabled={!tempCustomDateRange.from || !tempCustomDateRange.to}
                sx={{
                  fontSize: "0.7rem",
                  textTransform: "none",
                  borderRadius: 1,
                }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Collapse>
      </Menu>

      {/* ✅ Mobile/Desktop Conditional Rendering */}
      <InfiniteScroll
        dataLength={totalRowCount}
        next={fetchMoreData}
        hasMore={hasMore}
        height={"72vh"}
        loader={
          loading && (
            <Box sx={{ textAlign: "center", p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )
        }
        endMessage={
          items.length > 0 && (
            <Typography
              sx={{ textAlign: "center", p: 2, color: "text.secondary" }}
            >
              No more data
            </Typography>
          )
        }
      >
        {isMobile ? (
          /* 📱 MOBILE VIEW - Collapsible Cards */
          <Box sx={{ bgcolor: "background.paper" }}>
            {loading && items.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <CircularProgress size={40} />
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Loading data...
                </Typography>
              </Box>
            ) : items.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <Typography variant="h6" color="text.secondary">
                  No Data Found
                </Typography>
              </Box>
            ) : (
              (() => {
                let issueSerialNo = 0;

                return items.map((issue) => {
                  const itemsList = issue.items || [];
                  issueSerialNo++;

                  return (
                    <Box key={issue.id}>
                      {/* Issue Header Card */}
                      <Box
                        onClick={() =>
                          setExpandedIssue(
                            expandedIssue === issue.slip_no ? null : issue.slip_no
                          )
                        }
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          p: 1.5,
                          cursor: "pointer",
                          bgcolor: issueSerialNo % 2 === 0 ? "#fafafa" : "#ffffff",
                          "&:active": { bgcolor: "#e3f2fd" },
                          borderLeft: "4px solid #2196f3",
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 0.5,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "0.7rem",
                                color: "#666",
                                minWidth: 25,
                              }}
                            >
                              #{issueSerialNo}
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: "bold",
                                fontSize: "0.85rem",
                                flex: 1,
                              }}
                            >
                              {issue.slip_no}
                            </Typography>
                          </Box>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              color: "#666",
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <span style={{ fontWeight: 500 }}>
                              {issue.division_name || "N/A"}
                            </span>
                            <span style={{ color: "#999" }}>•</span>
                            <span style={{ fontSize: "0.7rem" }}>
                              {formatOnlyDate(issue.created_at)}
                            </span>
                          </Typography>
                        </Box>
                        <IconButton size="small" sx={{ ml: 1 }}>
                          {expandedIssue === issue.slip_no ? (
                            <ExpandLess fontSize="small" />
                          ) : (
                            <ExpandMore fontSize="small" />
                          )}
                        </IconButton>
                      </Box>

                      {/* Issue Details - Collapsible */}
                      <Collapse
                        in={expandedIssue === issue.slip_no}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Box
                          sx={{
                            px: 1.5,
                            py: 1,
                            bgcolor:
                              issueSerialNo % 2 === 0 ? "#f8f9fa" : "#fafafa",
                            borderLeft: "4px solid #2196f3",
                          }}
                        >
                          {/* Issue Level Info */}
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gap: 0.8,
                              mb: 1.5,
                              pb: 1,
                              borderBottom: "2px solid #e0e0e0",
                            }}
                          >
                            <Box>
                              <Typography
                                sx={{
                                  fontSize: "0.6rem",
                                  color: "#999",
                                  letterSpacing: "0.5px",
                                }}
                              >
                                SLIP NO
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "0.75rem",
                                  fontWeight: 600,
                                  color: "#1976d2",
                                }}
                              >
                                {issue.slip_no}
                              </Typography>
                            </Box>

                            <Box>
                              <Typography
                                sx={{
                                  fontSize: "0.6rem",
                                  color: "#999",
                                  letterSpacing: "0.5px",
                                }}
                              >
                                DATE
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "0.72rem",
                                  fontWeight: 500,
                                  color: "#555",
                                }}
                              >
                                {formatOnlyDate(issue.created_at)}
                              </Typography>
                            </Box>

                            <Box>
                              <Typography
                                sx={{
                                  fontSize: "0.6rem",
                                  color: "#999",
                                  letterSpacing: "0.5px",
                                }}
                              >
                                DIVISION
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "0.72rem",
                                  fontWeight: 500,
                                  color: "#555",
                                }}
                              >
                                {issue.division_name || "-"}
                              </Typography>
                            </Box>

                            <Box>
                              <Typography
                                sx={{
                                  fontSize: "0.6rem",
                                  color: "#999",
                                  letterSpacing: "0.5px",
                                }}
                              >
                                PROJECT
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "0.72rem",
                                  fontWeight: 500,
                                  color: "#555",
                                }}
                              >
                                {issue.project_name || "-"}
                              </Typography>
                            </Box>

                            <Box sx={{ gridColumn: "1 / -1" }}>
                              <Typography
                                sx={{
                                  fontSize: "0.6rem",
                                  color: "#999",
                                  letterSpacing: "0.5px",
                                }}
                              >
                                TOTAL ITEMS
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "0.72rem",
                                  fontWeight: 600,
                                  color: "#4caf50",
                                }}
                              >
                                {itemsList.length}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Items List */}
                          <Typography
                            sx={{
                              fontSize: "0.65rem",
                              fontWeight: 600,
                              color: "#666",
                              mb: 1,
                              letterSpacing: "0.5px",
                            }}
                          >
                            ITEMS ({itemsList.length})
                          </Typography>

                          {itemsList.map((item, itemIndex) => (
                            <Box
                              key={itemIndex}
                              sx={{
                                mb: 1.5,
                                p: 1,
                                bgcolor: "#fff",
                                borderRadius: "4px",
                                border: "1px solid #e0e0e0",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "0.75rem",
                                  fontWeight: 600,
                                  color: "#1976d2",
                                  mb: 0.8,
                                }}
                              >
                                Item #{itemIndex + 1}
                              </Typography>

                              <Box
                                sx={{
                                  display: "grid",
                                  gridTemplateColumns: "1fr 1fr",
                                  gap: 0.8,
                                }}
                              >
                                <Box sx={{ gridColumn: "1 / -1" }}>
                                  <Typography
                                    sx={{
                                      fontSize: "0.6rem",
                                      color: "#999",
                                      letterSpacing: "0.5px",
                                    }}
                                  >
                                    ITEM NAME
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: "0.72rem",
                                      fontWeight: 500,
                                      color: "#333",
                                    }}
                                  >
                                    {item.item_name || "-"}
                                  </Typography>
                                </Box>

                                <Box>
                                  <Typography
                                    sx={{
                                      fontSize: "0.6rem",
                                      color: "#999",
                                      letterSpacing: "0.5px",
                                    }}
                                  >
                                    CATEGORY
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: "0.7rem",
                                      fontWeight: 500,
                                      color: "#555",
                                    }}
                                  >
                                    {item.category_name || "-"}
                                  </Typography>
                                </Box>

                                <Box>
                                  <Typography
                                    sx={{
                                      fontSize: "0.6rem",
                                      color: "#999",
                                      letterSpacing: "0.5px",
                                    }}
                                  >
                                    SUB CATEGORY
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: "0.7rem",
                                      fontWeight: 500,
                                      color: "#555",
                                    }}
                                  >
                                    {item.sub_category_name || "-"}
                                  </Typography>
                                </Box>

                                <Box>
                                  <Typography
                                    sx={{
                                      fontSize: "0.6rem",
                                      color: "#999",
                                      letterSpacing: "0.5px",
                                    }}
                                  >
                                    ITEM TYPE
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: "0.7rem",
                                      fontWeight: 500,
                                      color: "#555",
                                    }}
                                  >
                                    {item.item_type || "-"}
                                  </Typography>
                                </Box>

                                <Box>
                                  <Typography
                                    sx={{
                                      fontSize: "0.6rem",
                                      color: "#999",
                                      letterSpacing: "0.5px",
                                    }}
                                  >
                                    QTY ISSUED
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: "0.72rem",
                                      fontWeight: 600,
                                      color: "#4caf50",
                                    }}
                                  >
                                    {item.issue_qty || 0}
                                  </Typography>
                                </Box>

                                <Box>
                                  <Typography
                                    sx={{
                                      fontSize: "0.6rem",
                                      color: "#999",
                                      letterSpacing: "0.5px",
                                    }}
                                  >
                                    UNIT
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: "0.72rem",
                                      fontWeight: 500,
                                      color: "#555",
                                    }}
                                  >
                                    {item.uom_name || "-"}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Collapse>
                      <Divider />
                    </Box>
                  );
                });
              })()
            )}
          </Box>
        ) : (
          /* 💻 DESKTOP VIEW - Original Table */
          <TableContainer
            component={Paper}
            id="item-table"
            sx={{
              maxHeight: "72vh",
              overflowX: { xs: "auto", sm: "unset" },
              overflowY: "auto",
              width: "100%",
            }}
          >
            <Table
              stickyHeader
              size="small"
              sx={{
                minWidth: "max-content",
                "& td, & th": {
                  borderBottom: "1px solid #ccc",
                  textAlign: "center",
                  whiteSpace: "nowrap",
                },
              }}
            >
              {/* ========== TABLE HEAD ========== */}
              <TableHead
                sx={{
                  "& th": {
                    fontWeight: "bold",
                    color: (theme) =>
                      theme.palette.mode === "dark" ? "#2196f3" : "#333",
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark" ? "black" : "#f5f5f5",
                  },
                }}
              >
                <TableRow>
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 0,
                      backgroundColor: "#f5f5f5",
                      zIndex: 3,
                      minWidth: 50,
                      padding: "8px",
                    }}
                  >
                    S. No.
                  </TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Req. Slip No</TableCell>
                  <TableCell>Division</TableCell>
                  <TableCell>Project</TableCell>
                  <TableCell>Category</TableCell>
                  {/* <TableCell>Sub Category</TableCell> */}
                  <TableCell>Item Type</TableCell>
                  <TableCell>Item Name</TableCell>
                  <TableCell align="right">Qty Issued</TableCell>
                  <TableCell>Unit</TableCell>
                </TableRow>
              </TableHead>

              {/* ========== TABLE BODY ========== */}
              <TableBody>
                {loading && items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 8 }}>
                      <CircularProgress size={40} />
                      <Typography variant="body2" sx={{ mt: 2 }}>
                        Loading data...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 8 }}>
                      <Box>
                        <Typography variant="h6" color="text.secondary">
                          No Data Found
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {(() => {
                      let issueSerialNo = 0;

                      return items.flatMap((issue) => {
                        const itemsList = issue.items || [];
                        issueSerialNo++;

                        return itemsList.map((item, itemIdx) => {
                          const isFirstItem = itemIdx === 0;

                          return (
                            <TableRow
                              key={`${issue.id}-${itemIdx}`}
                              hover
                              sx={{
                                backgroundColor:
                                  issueSerialNo % 2 === 0
                                    ? "#fafafa"
                                    : "#ffffff",
                                "&:hover": {
                                  backgroundColor: "#e3f2fd",
                                  "& td": {
                                    backgroundColor: "#e3f2fd !important",
                                  },
                                },
                                "& td": {
                                  paddingTop: "4px !important",
                                  paddingBottom: "4px !important",
                                  fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                                  whiteSpace: "nowrap",
                                  color: (theme) =>
                                    theme.palette.mode === "dark"
                                      ? "white"
                                      : "inherit",
                                },
                              }}
                            >
                              {/* Serial Number - Rowspan */}
                              {isFirstItem && (
                                <TableCell
                                  rowSpan={itemsList.length}
                                  align="center"
                                  sx={{
                                    position: "sticky",
                                    left: 0,
                                    backgroundColor:
                                      issueSerialNo % 2 === 0
                                        ? "#fafafa"
                                        : "#ffffff",
                                    zIndex: 2,
                                    fontWeight: "600",
                                    verticalAlign: "middle",
                                  }}
                                >
                                  {issueSerialNo}
                                </TableCell>
                              )}

                              {/* Date - Rowspan */}
                              {isFirstItem && (
                                <TableCell
                                  rowSpan={itemsList.length}
                                  sx={{ verticalAlign: "middle" }}
                                >
                                  {formatOnlyDate(issue.created_at)}
                                </TableCell>
                              )}

                              {/* Slip No - Rowspan */}
                              {isFirstItem && (
                                <TableCell
                                  rowSpan={itemsList.length}
                                  sx={{
                                    verticalAlign: "middle",
                                  }}
                                >
                                  <Tooltip
                                    title={`${itemsList.length} item(s)`}
                                    arrow
                                  >
                                    <span>{issue.slip_no}</span>
                                  </Tooltip>
                                </TableCell>
                              )}

                              {/* Division - Rowspan */}
                              {isFirstItem && (
                                <TableCell
                                  rowSpan={itemsList.length}
                                  sx={{ verticalAlign: "middle" }}
                                >
                                  {issue.division_name}
                                </TableCell>
                              )}

                              {/* Project - Rowspan */}
                              {isFirstItem && (
                                <TableCell
                                  rowSpan={itemsList.length}
                                  sx={{ verticalAlign: "middle" }}
                                >
                                  {issue.project_name || "-"}
                                </TableCell>
                              )}

                              {/* Item Details */}
                              <TableCell>{item.category_name || "-"}</TableCell>
                              <TableCell>
                                {item.sub_category_name || "-"}
                              </TableCell>
                              <TableCell>{item.item_type || "-"}</TableCell>
                              <TableCell>
                                <Tooltip title={item.item_name} arrow>
                                  <span>{item.item_name || "-"}</span>
                                </Tooltip>
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: "500" }}>
                                {item.issue_qty || 0}
                              </TableCell>
                              <TableCell>{item.uom_name || "-"}</TableCell>
                            </TableRow>
                          );
                        });
                      });
                    })()}
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </InfiniteScroll>
    </Box>
  );
}

export default Material_issue_register;